import { LegalLayout } from "@/layouts/LegalLayout";

export function PrivacyPage() {
  return (
    <LegalLayout
      title="Política de Privacidade"
      lastUpdated="19 de abril de 2026"
    >
      <h2>1. Introdução</h2>
      <p>
        Esta Política de Privacidade descreve como o <strong>Cyphron</strong>{" "}
        — operado por <strong>Henrique Almeida Porfirio</strong> (pessoa
        física, CNPJ em processo de registro), doravante &ldquo;Cyphron&rdquo;
        ou &ldquo;nós&rdquo; — coleta, utiliza, armazena, compartilha e protege
        dados pessoais, em observância à <strong>Lei Geral de Proteção de
        Dados Pessoais (Lei nº 13.709/2018 — &ldquo;LGPD&rdquo;)</strong>.
      </p>
      <p>
        Esta Política aplica-se ao site{" "}
        <strong>cyphronsec.com</strong>, à API{" "}
        <strong>api.cyphronsec.com</strong> e a todos os serviços prestados
        pela Plataforma.
      </p>

      <h2>2. Papel do Cyphron (Controlador vs. Operador)</h2>
      <p>
        O Cyphron atua com <strong>duplo papel</strong> conforme o tipo de
        dado tratado:
      </p>
      <ul>
        <li>
          <strong>Controlador</strong> em relação aos dados pessoais dos
          Clientes contratantes e dos Usuários Autorizados que acessam a
          Plataforma (dados de cadastro, autenticação, faturamento, suporte).
        </li>
        <li>
          <strong>Operador</strong> em relação aos dados que o Cliente,
          voluntariamente, insira na Plataforma para monitoramento (por
          exemplo, domínios e endpoints que podem incidentalmente conter dados
          pessoais em headers ou logs). Nesses casos, o <strong>Cliente é o
          Controlador</strong> e o Cyphron processa tais dados estritamente
          conforme suas instruções e as finalidades da relação contratual.
        </li>
      </ul>

      <h2>3. Dados que Coletamos</h2>

      <h3>3.1 Dados de Cadastro e Faturamento</h3>
      <ul>
        <li>Nome da empresa ou razão social informada no cadastro;</li>
        <li>
          E-mail de contato do responsável pela conta (usado também como
          identificador de login do cliente);
        </li>
        <li>
          Dados de pagamento e dados fiscais porventura exigidos para
          emissão da cobrança são coletados e processados{" "}
          <strong>exclusivamente pela Stripe</strong> no ambiente de checkout.
          O Cyphron <strong>não armazena</strong> número de cartão, CVV,
          endereço completo ou documento fiscal em seus servidores — retém
          apenas o identificador do cliente na Stripe, o identificador da
          assinatura, o status (ativo, inadimplente, cancelado) e metadados
          mínimos necessários à gestão do ciclo de vida da assinatura.
        </li>
      </ul>
      <p className="note">
        Caso, no futuro, o Cyphron passe a coletar diretamente documento
        fiscal (CPF/CNPJ), telefone ou endereço — por exemplo, para emissão
        de nota fiscal própria — esta Política será atualizada previamente e
        o tratamento passará a ocorrer sob base legal de cumprimento de
        obrigação legal (art. 7º, II LGPD).
      </p>

      <h3>3.2 Dados de Autenticação e Acesso</h3>
      <ul>
        <li>
          E-mail e senha (armazenada com hash por meio do provedor{" "}
          <strong>Supabase Auth</strong>);
        </li>
        <li>Tokens de sessão JWT;</li>
        <li>
          Dados de autenticação multifator TOTP (apenas para contas de
          operador administrador);
        </li>
        <li>
          Endereço IP, user-agent, timestamp de acesso, rotas acessadas (logs
          de auditoria — Lei nº 12.965/2014, Marco Civil da Internet).
        </li>
      </ul>

      <h3>3.3 Dados Fornecidos pelo Cliente para Monitoramento</h3>
      <ul>
        <li>Domínios e subdomínios cadastrados;</li>
        <li>Endereços IP e endpoints HTTP/HTTPS;</li>
        <li>
          Tokens de API de terceiros fornecidos voluntariamente (por exemplo,
          Cloudflare API Token), armazenados <strong>criptografados em
          repouso</strong> via Fernet (AES-128 em modo CBC com HMAC);
        </li>
        <li>
          Configurações de alertas (destinatários de e-mail, webhook do
          Telegram quando habilitado).
        </li>
      </ul>

      <h3>3.4 Dados Gerados pelo Serviço (Dados de Monitoramento)</h3>
      <ul>
        <li>
          Resultados de verificações de uptime, SSL/TLS, headers de segurança,
          DNS;
        </li>
        <li>Lista de tecnologias detectadas nos endpoints monitorados;</li>
        <li>
          CVEs (Common Vulnerabilities and Exposures) associadas às
          tecnologias detectadas;
        </li>
        <li>Relatórios mensais em PDF armazenados para consulta posterior;</li>
        <li>Histórico de alertas e incidentes.</li>
      </ul>

      <h3>3.5 Dados Coletados pelo Formulário de Contato</h3>
      <ul>
        <li>Nome, e-mail, empresa, cargo e mensagem de interesse.</li>
      </ul>

      <h2>4. Finalidades e Bases Legais</h2>
      <table>
        <thead>
          <tr>
            <th>Finalidade</th>
            <th>Base legal (LGPD)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Execução do contrato de prestação de serviço</td>
            <td>Art. 7º, V — execução de contrato</td>
          </tr>
          <tr>
            <td>Faturamento, cobrança e cumprimento de obrigações fiscais</td>
            <td>Art. 7º, II — cumprimento de obrigação legal</td>
          </tr>
          <tr>
            <td>Autenticação, segurança e prevenção à fraude</td>
            <td>Art. 7º, IX — legítimo interesse</td>
          </tr>
          <tr>
            <td>Logs de auditoria (Marco Civil)</td>
            <td>Art. 7º, II — cumprimento de obrigação legal</td>
          </tr>
          <tr>
            <td>Suporte técnico e atendimento ao cliente</td>
            <td>Art. 7º, V — execução de contrato</td>
          </tr>
          <tr>
            <td>Resposta a leads e formulário de contato</td>
            <td>Art. 7º, I — consentimento</td>
          </tr>
          <tr>
            <td>
              Melhoria do produto com base em dados agregados e anonimizados
            </td>
            <td>Art. 7º, IX — legítimo interesse</td>
          </tr>
        </tbody>
      </table>

      <h2>5. Compartilhamento com Subprocessadores</h2>
      <p>
        Para viabilizar o Serviço, o Cyphron utiliza os seguintes
        subprocessadores. Todos operam sob contratos que exigem padrões
        adequados de proteção de dados e são auditáveis mediante requisição.
      </p>
      <table>
        <thead>
          <tr>
            <th>Subprocessador</th>
            <th>Finalidade</th>
            <th>Localização</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Supabase, Inc.</td>
            <td>Banco de dados PostgreSQL e autenticação</td>
            <td>Estados Unidos (US-East)</td>
          </tr>
          <tr>
            <td>Fly.io, Inc.</td>
            <td>Hospedagem da API backend e workers</td>
            <td>Brasil (GRU — São Paulo)</td>
          </tr>
          <tr>
            <td>Vercel, Inc.</td>
            <td>Hospedagem da interface web (frontend)</td>
            <td>Estados Unidos (rede global de edge)</td>
          </tr>
          <tr>
            <td>Cloudflare, Inc.</td>
            <td>
              CDN, DNS, proteção contra DDoS, gerenciamento de domínio,
              roteamento de e-mail
            </td>
            <td>Rede global distribuída</td>
          </tr>
          <tr>
            <td>Stripe, Inc.</td>
            <td>Processamento de pagamentos (PCI-DSS Nível 1)</td>
            <td>Estados Unidos</td>
          </tr>
          <tr>
            <td>Upstash, Inc.</td>
            <td>Cache em memória (Redis) e filas de tarefas</td>
            <td>Estados Unidos</td>
          </tr>
          <tr>
            <td>Sentry (Functional Software, Inc.)</td>
            <td>Monitoramento de erros em produção</td>
            <td>Estados Unidos</td>
          </tr>
          <tr>
            <td>Better Stack (Logtail, s.r.o.)</td>
            <td>Agregação de logs e monitoramento de uptime</td>
            <td>União Europeia (República Tcheca)</td>
          </tr>
          <tr>
            <td>Google LLC (Gmail SMTP)</td>
            <td>Envio de e-mails transacionais</td>
            <td>Estados Unidos</td>
          </tr>
        </tbody>
      </table>

      <h2>6. Transferência Internacional de Dados</h2>
      <p>
        Alguns dos subprocessadores acima estão localizados fora do território
        brasileiro. Nos termos do art. 33 da LGPD, tais transferências ocorrem
        sob as seguintes salvaguardas, conforme o caso:
      </p>
      <ul>
        <li>
          <strong>Cláusulas contratuais específicas</strong> com obrigações de
          confidencialidade, segurança e respeito aos princípios da LGPD;
        </li>
        <li>
          <strong>Garantias equivalentes</strong> de proteção, uma vez que os
          subprocessadores adotam frameworks reconhecidos (SOC 2, ISO 27001,
          GDPR) em nível compatível ao da LGPD;
        </li>
        <li>
          <strong>Execução de contrato</strong> entre as partes (art. 33, V).
        </li>
      </ul>
      <p>
        O Cliente está ciente e consente com tais transferências como condição
        necessária à prestação do Serviço.
      </p>

      <h2>7. Retenção de Dados</h2>
      <table>
        <thead>
          <tr>
            <th>Categoria</th>
            <th>Prazo de retenção</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Dados de cadastro e faturamento</td>
            <td>
              Durante a vigência do contrato + 5 anos (art. 27 CDC / art. 206
              §5º, I CC)
            </td>
          </tr>
          <tr>
            <td>Logs de acesso (Marco Civil)</td>
            <td>6 meses (art. 15 Marco Civil da Internet)</td>
          </tr>
          <tr>
            <td>Dados de monitoramento ativos</td>
            <td>Durante a vigência do contrato</td>
          </tr>
          <tr>
            <td>
              Relatórios e histórico de alertas após cancelamento do contrato
            </td>
            <td>
              90 dias (para recuperação em caso de reativação), após o que são
              anonimizados ou excluídos
            </td>
          </tr>
          <tr>
            <td>Tokens de API de terceiros fornecidos pelo Cliente</td>
            <td>Excluídos em até 30 dias após o término contratual</td>
          </tr>
          <tr>
            <td>Backups</td>
            <td>Até 35 dias (política do provedor de banco)</td>
          </tr>
        </tbody>
      </table>

      <h2>8. Segurança</h2>
      <p>
        O Cyphron adota medidas técnicas e organizacionais de segurança
        compatíveis com o estado da arte, incluindo:
      </p>
      <ul>
        <li>
          Criptografia em trânsito (<strong>TLS 1.2+</strong>) em todas as
          conexões;
        </li>
        <li>
          Criptografia em repouso para tokens de API de terceiros (
          <strong>Fernet / AES-128</strong>);
        </li>
        <li>
          Armazenamento de senhas com hash forte, gerenciado pelo Supabase
          Auth;
        </li>
        <li>
          Autenticação multifator obrigatória para contas administrativas;
        </li>
        <li>
          Princípio do menor privilégio para acesso a infraestrutura
          (isolamento de redes, secrets gerenciados via cofre do provedor);
        </li>
        <li>
          Logs de auditoria imutáveis para ações administrativas sensíveis;
        </li>
        <li>
          Monitoramento contínuo de erros e anomalias (Sentry, Better Stack);
        </li>
        <li>Backups automatizados com criptografia.</li>
      </ul>
      <p className="note">
        <strong>Comunicação de incidentes:</strong> em caso de incidente de
        segurança que possa acarretar risco ou dano relevante aos titulares, o
        Cyphron notificará a <strong>ANPD</strong> e os titulares afetados em
        prazo compatível com o recomendado pela Autoridade Nacional (referência
        atual: 3 dias úteis a partir do conhecimento).
      </p>

      <h2>9. Direitos do Titular (LGPD art. 18)</h2>
      <p>
        Nos termos da LGPD, o titular dos dados pessoais pode, a qualquer
        momento e mediante requisição, exercer os seguintes direitos:
      </p>
      <ul>
        <li>Confirmação da existência de tratamento;</li>
        <li>Acesso aos dados;</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
        <li>
          Anonimização, bloqueio ou eliminação de dados desnecessários ou
          tratados em desconformidade;
        </li>
        <li>
          Portabilidade dos dados a outro fornecedor, observados os segredos
          comercial e industrial;
        </li>
        <li>
          Eliminação dos dados tratados com base no consentimento, exceto nas
          hipóteses de conservação legal;
        </li>
        <li>
          Informação sobre entidades públicas e privadas com as quais houve
          compartilhamento;
        </li>
        <li>
          Informação sobre a possibilidade de não fornecer consentimento e
          sobre as consequências da negativa;
        </li>
        <li>Revogação do consentimento, quando for a base legal aplicável.</li>
      </ul>
      <p>
        Os pedidos devem ser encaminhados ao canal oficial em item 12 e serão
        respondidos em prazo não superior a 15 (quinze) dias, observadas as
        exceções previstas em lei.
      </p>

      <h2>10. Cookies e Tecnologias Similares</h2>
      <p>O Cyphron utiliza cookies estritamente necessários para:</p>
      <ul>
        <li>Autenticação e manutenção de sessão;</li>
        <li>Proteção contra CSRF;</li>
        <li>Armazenamento local de preferências de interface.</li>
      </ul>
      <p>
        Não utilizamos cookies de publicidade, <em>tracking</em> comportamental
        ou analytics de terceiros direcionados a perfilização do usuário. Caso
        tais tecnologias venham a ser adicionadas no futuro, esta Política
        será atualizada e o consentimento adequado será requerido quando a
        lei exigir.
      </p>

      <h2>11. Alterações desta Política</h2>
      <p>
        O Cyphron pode atualizar esta Política a qualquer momento. Alterações
        materiais serão comunicadas por e-mail ou pelo painel da Plataforma
        com antecedência mínima de 15 (quinze) dias. A data da última
        atualização está indicada no topo deste documento.
      </p>

      <h2>12. Contato — Encarregado (DPO)</h2>
      <p>
        Para exercer direitos como titular, esclarecer dúvidas ou formalizar
        reclamações relativas ao tratamento de dados pessoais, entre em
        contato pelo canal:
      </p>
      <p>
        <strong>Encarregado pelo tratamento de dados:</strong> Henrique
        Almeida Porfirio
        <br />
        <strong>E-mail:</strong>{" "}
        <a href="mailto:support@cyphronsec.com">support@cyphronsec.com</a>
      </p>
      <p>
        Caso a resposta não seja satisfatória, o titular poderá apresentar
        reclamação à <strong>Autoridade Nacional de Proteção de Dados
        (ANPD)</strong> por meio do site{" "}
        <a
          href="https://www.gov.br/anpd"
          target="_blank"
          rel="noopener noreferrer"
        >
          gov.br/anpd
        </a>
        .
      </p>
    </LegalLayout>
  );
}
